// src/pages/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardService } from '../../services/dashboardServices';
import { companyService, userService } from '../../services/apiServices';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
  ActivityChart, 
  UserDistributionChart,
  MetricsCard
} from '../../components/dashboard/DashboardCharts';
import {
  UserEngagementChart,
  PerformanceMetricsChart
} from '../../components/dashboard/EnhancedCharts';

// Enhanced styles with animations for Admin Dashboard
const adminDashboardStyles = `
  /* Dashboard Container Animation */
  .admin-dashboard {
    animation: fadeInUp 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  
  @keyframes fadeInUp {
    0% {
      opacity: 0;
      transform: translateY(30px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Header Animation */
  .dashboard-header {
    animation: slideInFromLeft 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  
  @keyframes slideInFromLeft {
    0% {
      opacity: 0;
      transform: translateX(-50px);
    }
    100% {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  /* Metrics Cards Animation */
  .metrics-grid {
    animation: staggerIn 0.6s ease-out;
  }
  
  .metric-card {
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    overflow: hidden;
  }
  
  .metric-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.6s ease;
  }
  
  .metric-card:hover::before {
    left: 100%;
  }
  
  .metric-card:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
  
  /* Subscription Alert Animation */
  .subscription-alert {
    animation: slideInFromTop 0.5s ease-out;
    position: relative;
  }
  
  .subscription-alert::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 4px;
    background: linear-gradient(180deg, #ef4444, #f97316, #eab308);
    animation: pulseGradient 2s infinite;
  }
  
  @keyframes slideInFromTop {
    0% {
      opacity: 0;
      transform: translateY(-20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @keyframes pulseGradient {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }
  
  /* Charts Animation */
  .charts-grid {
    animation: fadeInScale 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  
  @keyframes fadeInScale {
    0% {
      opacity: 0;
      transform: scale(0.95);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
  
  .chart-card {
    transition: all 0.3s ease;
    border: 1px solid transparent;
  }
  
  .chart-card:hover {
    border-color: #e5e7eb;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
  
  /* Activities Table Animation */
  .activities-card {
    animation: slideInFromBottom 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }
  
  @keyframes slideInFromBottom {
    0% {
      opacity: 0;
      transform: translateY(30px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .activity-row {
    transition: all 0.2s ease;
  }
  
  .activity-row:hover {
    background: linear-gradient(90deg, rgba(59, 130, 246, 0.05), transparent);
    transform: translateX(4px);
  }
  
  /* Badge Animations */
  .activity-badge {
    transition: all 0.2s ease;
    animation: fadeIn 0.3s ease;
  }
  
  .activity-badge:hover {
    transform: scale(1.05);
  }
  
  @keyframes fadeIn {
    0% { opacity: 0; }
    100% { opacity: 1; }
  }
  
  /* Refresh Button Animation */
  .refresh-button {
    transition: all 0.3s ease;
  }
  
  .refresh-button:hover {
    transform: rotate(180deg) scale(1.1);
  }
  
  /* Loading Animation Enhancement */
  .loading-container {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .loading-content {
    text-align: center;
    color: white;
  }
  
  .loading-spinner {
    position: relative;
    margin: 0 auto 2rem;
  }
  
  .loading-spinner::before {
    content: '';
    position: absolute;
    top: -10px;
    left: -10px;
    right: -10px;
    bottom: -10px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 50%;
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.1); opacity: 0.7; }
  }
  
  /* Stagger Animation for Grid Items */
  .stagger-item {
    animation: staggerFadeIn 0.6s ease-out;
  }
  
  .stagger-item:nth-child(1) { animation-delay: 0.1s; }
  .stagger-item:nth-child(2) { animation-delay: 0.2s; }
  .stagger-item:nth-child(3) { animation-delay: 0.3s; }
  .stagger-item:nth-child(4) { animation-delay: 0.4s; }
  
  @keyframes staggerFadeIn {
    0% {
      opacity: 0;
      transform: translateY(20px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  /* Responsive Animations */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }
  }
`;

// Icons for metric cards
const ICONS = {
  user: "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
  analysis: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
  subscription: "M13 10V3L4 14h7v7l9-11h-7z",
  activity: "M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
};

const AdminDashboard = () => {
  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    metrics: {
      totalUsers: 0,
      totalAnalyses: 0,
      recentActivitiesCount: 0,
      subscriptionDaysRemaining: 0
    },
    historicalMetrics: {
      totalUsers: 0,
      totalAnalyses: 0,
      recentActivitiesCount: 0,
      subscriptionDaysRemaining: 0
    },
    recentActivities: [],
    userDistribution: [],
    activityTrends: [],
    engagementData: [],
    performanceData: [],
    companyInfo: {}
  });
  
  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch dashboard data on component mount
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch provider metrics using the dashboard service
        const response = await dashboardService.getProviderMetrics();
        let metricsData;
        
        if (response.success) {
          metricsData = response.data;
        } else {
          throw new Error('Failed to fetch dashboard metrics');
        }

        // Fetch historical data for trend calculation
        let historicalData = {};
        try {
          const historicalResponse = await dashboardService.getHistoricalMetrics(7);
          if (historicalResponse.success) {
            historicalData = historicalResponse.data.previousPeriod || {};
          }
        } catch (err) {
          console.warn('Historical data not available');
          historicalData = {};
        }

        // Prepare user distribution data for charts
        const userDistribution = [
          { name: 'Admins', value: metricsData.usersByRole?.admin || 0 },
          { name: 'Analystes', value: metricsData.usersByRole?.analyst || metricsData.usersByRole?.simpleuser || 0 },
          { name: 'Consultants', value: metricsData.usersByRole?.viewer || 0 }
        ];
        
        // Use real activity trends data from API
        const activityTrends = metricsData.activityTrends || [];
        
        // Use real user engagement data from API
        const engagementData = metricsData.userEngagement || [];
        
        // Sample performance metrics data for company
        const performanceData = [
          { subject: 'Analyses Complétées', value: 85 },
          { subject: 'Utilisateurs Actifs', value: 70 },
          { subject: 'Conformité EBIOS', value: 90 },
          { subject: 'Temps de Réponse', value: 75 },
          { subject: 'Satisfaction', value: 88 }
        ];
        
        setDashboardData({
          metrics: {
            totalUsers: metricsData.totalUsers || 0,
            totalAnalyses: metricsData.totalAnalyses || metricsData.activeAnalyses || 0,
            recentActivitiesCount: metricsData.recentActivitiesCount || 0,
            subscriptionDaysRemaining: metricsData.subscriptionDaysRemaining || 0
          },
          historicalMetrics: {
            totalUsers: historicalData.totalUsers || 0,
            totalAnalyses: historicalData.totalAnalyses || historicalData.activeAnalyses || 0,
            recentActivitiesCount: historicalData.recentActivitiesCount || 0,
            subscriptionDaysRemaining: historicalData.subscriptionDaysRemaining || 0
          },
          recentActivities: metricsData.recentActivities || [],
          userDistribution,
          activityTrends,
          engagementData,
          performanceData,
          companyInfo: metricsData.companyInfo || {}
        });
      } catch (error) {
        console.error('Dashboard data fetch error:', error);
        setError('Erreur lors du chargement des données. Veuillez réessayer plus tard.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);
  
  // Helper function to determine activity badge color
  const getActivityBadgeColor = (actionType) => {
    if (actionType.includes('LOGIN')) return 'bg-blue-100 text-blue-800';
    if (actionType.includes('CREATE')) return 'bg-green-100 text-green-800';
    if (actionType.includes('UPDATE')) return 'bg-yellow-100 text-yellow-800';
    if (actionType.includes('DELETE')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Helper function to get subscription status color
  const getSubscriptionStatusColor = (daysRemaining) => {
    if (daysRemaining <= 3) return 'text-red-600';
    if (daysRemaining <= 7) return 'text-yellow-600';
    return 'text-green-600';
  };

  // Show enhanced loading spinner while fetching data
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="loading-spinner">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent"></div>
          </div>
          <h2 className="text-2xl font-semibold mb-2">Chargement du tableau de bord...</h2>
          <p className="text-white/80">Récupération des données en cours</p>
          <div className="mt-4 flex justify-center space-x-1">
            <div className="w-2 h-2 bg-white rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
          </div>
        </div>
      </div>
    );
  }
  
  // Show enhanced error message if something went wrong
  if (error) {
    return (
      <div className="p-6 min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-red-100">
        <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full border border-red-200">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
              <svg className="h-8 w-8 text-red-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Erreur de chargement</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-3 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-300 font-medium flex items-center justify-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Réessayer</span>
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  const { metrics, historicalMetrics, recentActivities, userDistribution, activityTrends, engagementData, performanceData, companyInfo } = dashboardData;

  return (
    <div className="admin-dashboard p-6 bg-gradient-to-br from-gray-50 to-blue-50 min-h-screen">
      {/* Add custom styles */}
      <style>{adminDashboardStyles}</style>
      
      <div className="dashboard-header flex justify-between items-center mb-8">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Dashboard Administrateur
          </h1>
          {companyInfo.name && (
            <p className="text-xl text-gray-600 mt-2 font-medium">{companyInfo.name}</p>
          )}
        </div>
        <div className="flex items-center space-x-4">
          <div className="bg-white px-4 py-2 rounded-lg shadow-sm border">
            <div className="text-sm text-gray-500 flex items-center space-x-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Dernière mise à jour : {new Date().toLocaleString()}</span>
            </div>
          </div>
          <button 
            onClick={() => window.location.reload()}
            className="refresh-button p-3 rounded-lg text-gray-600 hover:text-white hover:bg-blue-600 bg-white shadow-sm border transition-all duration-300"
            title="Actualiser les données"
            disabled={isLoading}
          >
            <svg className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {/* Enhanced Subscription Status Alert */}
      {companyInfo.subscription && companyInfo.subscription.status === 'active' && (
        <div className={`subscription-alert mb-8 p-6 rounded-xl shadow-lg border-l-4 ${
          metrics.subscriptionDaysRemaining <= 3 
            ? 'bg-gradient-to-r from-red-50 to-red-100 border-red-500' 
            : metrics.subscriptionDaysRemaining <= 7 
              ? 'bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-500' 
              : 'bg-gradient-to-r from-green-50 to-green-100 border-green-500'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`p-3 rounded-full mr-4 ${
                metrics.subscriptionDaysRemaining <= 3 
                  ? 'bg-red-100' 
                  : metrics.subscriptionDaysRemaining <= 7 
                    ? 'bg-yellow-100' 
                    : 'bg-green-100'
              }`}>
                <svg className={`w-6 h-6 ${getSubscriptionStatusColor(metrics.subscriptionDaysRemaining)}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div>
                <h3 className={`font-bold text-lg ${getSubscriptionStatusColor(metrics.subscriptionDaysRemaining)}`}>
                  Statut de l'abonnement
                </h3>
                <p className={`${getSubscriptionStatusColor(metrics.subscriptionDaysRemaining)} opacity-80`}>
                  {metrics.subscriptionDaysRemaining} jour(s) restant(s)
                </p>
              </div>
            </div>
            <Link 
              to="/admin/subscription"
              className="bg-white px-6 py-3 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 font-medium text-gray-700 hover:text-blue-600 flex items-center space-x-2"
            >
              <span>Gérer l'abonnement</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          </div>
        </div>
      )}
      
      {/* Enhanced Main Metrics Cards */}
      <div className="metrics-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="metric-card stagger-item bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Utilisateurs</p>
              <p className="text-3xl font-bold text-blue-600">{metrics.totalUsers}</p>
              <p className="text-xs text-gray-500 mt-1">Total des utilisateurs</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.user} />
              </svg>
            </div>
          </div>
          <Link 
            to="/admin/users"
            className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors duration-200"
          >
            Gérer les utilisateurs
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        <div className="metric-card stagger-item bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Analyses</p>
              <p className="text-3xl font-bold text-green-600">{metrics.totalAnalyses}</p>
              <p className="text-xs text-gray-500 mt-1">Analyses actives</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.analysis} />
              </svg>
            </div>
          </div>
          <Link 
            to="/admin/analyses"
            className="mt-4 inline-flex items-center text-green-600 hover:text-green-800 font-medium text-sm transition-colors duration-200"
          >
            Voir les analyses
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        <div className="metric-card stagger-item bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Activités Récentes</p>
              <p className="text-3xl font-bold text-purple-600">{metrics.recentActivitiesCount}</p>
              <p className="text-xs text-gray-500 mt-1">Dernières actions</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.activity} />
              </svg>
            </div>
          </div>
          <Link 
            to="/admin/activities"
            className="mt-4 inline-flex items-center text-purple-600 hover:text-purple-800 font-medium text-sm transition-colors duration-200"
          >
            Voir les logs
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>

        <div className="metric-card stagger-item bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 mb-1">Jours d'Abonnement</p>
              <p className={`text-3xl font-bold ${metrics.subscriptionDaysRemaining <= 7 ? 'text-red-600' : 'text-yellow-600'}`}>
                {metrics.subscriptionDaysRemaining}
              </p>
              <p className="text-xs text-gray-500 mt-1">Jours restants</p>
            </div>
            <div className={`p-3 rounded-full ${metrics.subscriptionDaysRemaining <= 7 ? 'bg-red-100' : 'bg-yellow-100'}`}>
              <svg className={`w-8 h-8 ${metrics.subscriptionDaysRemaining <= 7 ? 'text-red-600' : 'text-yellow-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS.subscription} />
              </svg>
            </div>
          </div>
          <Link 
            to="/admin/subscription"
            className={`mt-4 inline-flex items-center font-medium text-sm transition-colors duration-200 ${
              metrics.subscriptionDaysRemaining <= 7 
                ? 'text-red-600 hover:text-red-800' 
                : 'text-yellow-600 hover:text-yellow-800'
            }`}
          >
            Gérer l'abonnement
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
      
      {/* Enhanced Charts Grid */}
      <div className="charts-grid grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="chart-card bg-white p-6 rounded-xl shadow-lg">
          <ActivityChart data={activityTrends} />
        </div>
        <div className="chart-card bg-white p-6 rounded-xl shadow-lg">
          <UserDistributionChart data={userDistribution} />
        </div>
      </div>
      
      {/* Enhanced User Engagement Chart */}
      <div className="charts-grid grid grid-cols-1 gap-6 mb-8">
        <div className="chart-card bg-white p-6 rounded-xl shadow-lg">
          <UserEngagementChart data={engagementData} />
        </div>
      </div>
      
      {/* Enhanced Recent Activities Card */}
      <div className="activities-card bg-white p-8 rounded-xl shadow-lg mb-8 border border-gray-100">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Activités récentes</h2>
          </div>
          <div className="flex items-center space-x-3">
            <span className="bg-gradient-to-r from-blue-100 to-blue-200 text-blue-700 text-sm font-medium px-4 py-2 rounded-full">
              {recentActivities.length} activités
            </span>
          </div>
        </div>
        
        {recentActivities.length === 0 ? (
          <div className="text-center py-12">
            <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <p className="text-gray-500 text-lg">Aucune activité récente à afficher</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Action</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Utilisateur</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentActivities.map((activity, index) => (
                  <tr key={activity.id} className="activity-row" style={{animationDelay: `${index * 0.1}s`}}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`activity-badge px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getActivityBadgeColor(activity.actionType)}`}>
                        {activity.actionType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                            <span className="text-xs font-medium text-gray-600">
                              {(activity.userName || activity.userId || 'U').charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {activity.userName || activity.userId}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        <div className="mt-6 flex justify-end">
          <Link 
            to="/admin/activities" 
            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 inline-flex items-center font-medium transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <span>Voir tous les logs d'activité</span>
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </Link>
        </div>
      </div>


    </div>
  );
};

export default AdminDashboard;